import { bespokeTenants } from '@debbie/common/dist/bespoke/index.js';
import keys from '@debbie/common/dist/cache/keys.js';
import { enteredAmount, isAssociationManaged, } from '@debbie/common/dist/economy/case-vouchers/index.js';
import { FixedDistributionDateBehavior, maxSumAcrossPerspectives, } from '@debbie/common/dist/economy/fixed-distributions/index.js';
import { correctsExpired, counterEntryVoucherTypeId, isPayment, } from '@debbie/common/dist/economy/helpers/vouchers.js';
import { ErrorYouAreAboutToMakePossibleUnintendedAdjustments, VoucherCategory, } from '@debbie/common/dist/economy/vouchers/index.js';
import { isAdmin } from '@debbie/common/dist/permissions/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as caseVouchersAPI from '@debbie/cortex/dist/api/case-vouchers.js';
import { caseVouchersEntity } from '@debbie/cortex/dist/case-vouchers/index.js';
import { fixedDistributionsEntity, useFixedDistributionsByCaseId, } from '@debbie/cortex/dist/fixed-distributions/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useTenantId } from '@debbie/cortex/dist/tenant/selected.js';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { errorNotification } from '../../../utils/errors.js';
import { Button, ButtonGroup, Modal } from '../../index.js';
import CounterEntryModal from './CounterEntryModal.js';
const CaseVoucherInfoActions = (props) => {
    const lang = useSelector((s) => s.lang);
    const meRole = useRole();
    const tenantId = useTenantId();
    const [counterEntryModalOpen, setCounterEntryModalOpen] = useState(false);
    const [cascadeConfirmOpen, setCascadeConfirmOpen] = useState(false);
    const fixedDistributionsLoader = useFixedDistributionsByCaseId(props.caseVoucher.caseId);
    const fixedDistributions = h.hasData(fixedDistributionsLoader)
        ? fixedDistributionsLoader.data
        : null;
    const voucherId = props.caseVoucher.caseVoucherId;
    const voucherFds = (fixedDistributions ?? []).filter((fd) => fd.fromCaseVoucherId === voucherId || fd.toCaseVoucherId === voucherId);
    // Derived distributions are removed with the voucher without asking.
    const manualFds = voucherFds.filter((fd) => !fd.derived);
    const hasFixedDistributions = manualFds.length > 0;
    const hasAssociatedFds = manualFds.some((fd) => fd.associationId != null);
    const relevantFds = manualFds.filter((fd) => fd.fromCaseVoucherId === voucherId);
    const isFullyDistributed = relevantFds.length > 0 &&
        maxSumAcrossPerspectives(relevantFds) >=
            Math.abs(enteredAmount(props.caseVoucher));
    const isAssociationManagedVoucher = isAssociationManaged(props.caseVoucher);
    // A counter entry corrects the voucher from its origin.
    const counterable = !props.caseVoucher.expired ||
        correctsExpired({ voucherTypeId: counterEntryVoucherTypeId(props.caseVoucher) }, props.caseVoucher, FixedDistributionDateBehavior.FromOriginal);
    const deleteVoucherMutation = useMutation(async (cascade) => {
        const del = (preventAdjustments, cascadeFixedDistributions) => caseVouchersAPI.delCaseVoucher(props.caseVoucher.caseVoucherId, {
            preventAdjustments,
            cascadeFixedDistributions,
        });
        try {
            await del(true, cascade);
        }
        catch (error) {
            if (
            // @ts-ignore
            error?.message?.indexOf(ErrorYouAreAboutToMakePossibleUnintendedAdjustments) !== -1) {
                if (confirm(lang.billing.warnThisWillAffectBilling)) {
                    await del(false, cascade);
                }
            }
            else {
                throw error;
            }
        }
        q.invalidate(keys.write.modify(caseVouchersEntity, props.caseVoucher.caseVoucherId));
        q.invalidate(keys.write.create(fixedDistributionsEntity));
        setCascadeConfirmOpen(false);
        props.onDeleteVoucher && props.onDeleteVoucher();
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'delete', lang.errorEntities.caseVoucher);
        },
    });
    const markIrrecoverableMutation = useMutation(async () => {
        if (props.caseVoucher.caseId) {
            await caseVouchersAPI.patchCaseVoucher(props.caseVoucher.caseVoucherId, {
                caseId: props.caseVoucher.caseId,
                irrecoverableDate: moment().format(),
            });
            q.invalidate(keys.write.modify(caseVouchersEntity, props.caseVoucher.caseVoucherId));
        }
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.caseVoucher);
        },
    });
    const toggleExpiredMutation = useMutation(async () => {
        if (props.caseVoucher.caseId) {
            await caseVouchersAPI.patchCaseVoucher(props.caseVoucher.caseVoucherId, {
                caseId: props.caseVoucher.caseId,
                expired: !props.caseVoucher.expired,
            });
            q.invalidate(keys.write.modify(caseVouchersEntity, props.caseVoucher.caseVoucherId));
        }
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.caseVoucher);
        },
    });
    const restoreVoucherMutation = useMutation(async () => {
        await caseVouchersAPI.restoreCaseVoucher(props.caseVoucher.caseVoucherId);
        q.invalidate(keys.write.modify(caseVouchersEntity, props.caseVoucher.caseVoucherId));
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.caseVoucher);
        },
    });
    const loading = deleteVoucherMutation.isLoading ||
        markIrrecoverableMutation.isLoading ||
        toggleExpiredMutation.isLoading ||
        restoreVoucherMutation.isLoading;
    return (React.createElement(React.Fragment, null,
        !isAssociationManagedVoucher &&
            !props.caseVoucher.deletedAt &&
            (props.caseVoucher.category !== VoucherCategory.TRANSACTION ||
                (meRole && isAdmin(meRole))) && (React.createElement(Button, { theme: "dangerous", size: "mini", loading: loading, disabled: false, onClick: () => {
                if (hasFixedDistributions) {
                    setCascadeConfirmOpen(true);
                }
                else {
                    deleteVoucherMutation.mutate();
                }
            } }, lang.economy.voucherInfoDeleteVoucher)),
        React.createElement(ButtonGroup, { style: { float: 'right' } },
            (props.caseVoucher.category === VoucherCategory.PRINCIPAL ||
                isPayment(props.caseVoucher)) &&
                !isAssociationManagedVoucher &&
                props.caseVoucher.caseId &&
                !props.caseVoucher.deletedAt &&
                counterable &&
                fixedDistributions != null &&
                !isFullyDistributed && (React.createElement(Button, { size: "mini", loading: loading, onClick: () => setCounterEntryModalOpen(true) }, lang.economy.voucherInfoCreateCounterEntry)),
            !isAssociationManagedVoucher &&
                props.caseVoucher.category !== VoucherCategory.TRANSACTION &&
                !(props.caseVoucher.expired || props.caseVoucher.irrecoverableDate) && (React.createElement(Button, { size: "mini", loading: loading, onClick: () => {
                    markIrrecoverableMutation.mutate();
                } }, lang.economy.voucherInfoMarkVoucherIrrecoverable)),
            !isAssociationManagedVoucher &&
                props.caseVoucher.category !== VoucherCategory.TRANSACTION && (React.createElement(Button, { size: "mini", loading: loading, onClick: () => {
                    toggleExpiredMutation.mutate();
                } }, props.caseVoucher.expired
                ? lang.economy.voucherInfoUnmarkVoucherExpired
                : lang.economy.voucherInfoMarkVoucherExpired)),
            props.caseVoucher.deletedAt != null &&
                (tenantId === bespokeTenants.apcoaProd ||
                    tenantId === bespokeTenants.apcoaSandbox) && (React.createElement(Button, { size: "mini", loading: loading, onClick: () => restoreVoucherMutation.mutate() }, lang.economy.voucherInfoRestoreVoucher))),
        counterEntryModalOpen && (React.createElement(CounterEntryModal, { caseVoucherId: props.caseVoucher.caseVoucherId, 
            // A counter entry cancels the voucher from its origin, before
            // anything expired.
            fullAmount: Math.abs(enteredAmount(props.caseVoucher)), onClose: () => setCounterEntryModalOpen(false), onCreated: () => props.onDeleteVoucher?.() })),
        cascadeConfirmOpen && (React.createElement(Modal, { header: lang.economy.voucherInfoDeleteVoucher, maxWidth: "420px", onToggle: () => setCascadeConfirmOpen(false) },
            React.createElement("p", null, hasAssociatedFds
                ? lang.economy.voucherDeleteHasFixedDistributionsWithJoint
                : lang.economy.voucherDeleteHasFixedDistributions),
            React.createElement(Button, { theme: "dangerous", style: { width: '100%', marginTop: 12 }, loading: deleteVoucherMutation.isLoading, onClick: () => deleteVoucherMutation.mutate(true) }, lang.economy.voucherInfoDeleteVoucher)))));
};
export default CaseVoucherInfoActions;
//# sourceMappingURL=CaseVoucherInfoActions.js.map